import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Building2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyAuth = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const [registerData, setRegisterData] = useState({
    nom: '',
    email: '',
    password: '',
    confirmPassword: '',
    description: '',
    secteur: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/company/login`, loginData);
      toast.success('Connexion réussie!');
      localStorage.setItem('company_token', response.data.token);
      localStorage.setItem('company', JSON.stringify(response.data.company));
      navigate('/company/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (registerData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...dataToSend } = registerData;
      const response = await axios.post(`${API}/company/register`, dataToSend);
      toast.success('Inscription réussie!');
      localStorage.setItem('company_token', response.data.token);
      localStorage.setItem('company', JSON.stringify(response.data.company));
      navigate('/company/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-2xl mb-4">
            <Building2 className="h-8 w-8 text-white" strokeWidth={2} />
          </div>
          <h1 
            className="text-3xl font-bold text-slate-900" 
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            data-testid="company-auth-title"
          >
            Espace Employeur
          </h1>
          <p className="text-slate-600 mt-2">Publiez vos offres d'emploi et trouvez les meilleurs talents</p>
        </div>

        {/* Auth Card */}
        <Card className="p-8 shadow-xl">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="company-login-tab">Connexion</TabsTrigger>
              <TabsTrigger value="register" data-testid="company-register-tab">Inscription</TabsTrigger>
            </TabsList>

            {/* Login Form */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="company-login-email">Email</Label>
                  <Input
                    id="company-login-email"
                    type="email"
                    placeholder="entreprise@email.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="company-login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="company-login-password">Mot de passe</Label>
                  <Input
                    id="company-login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="company-login-password-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-6 font-semibold"
                  disabled={loading}
                  data-testid="company-login-submit-button"
                >
                  {loading ? 'Connexion...' : 'Se connecter'}
                </Button>
              </form>
            </TabsContent>

            {/* Register Form */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company-register-nom">Nom de l'entreprise</Label>
                    <Input
                      id="company-register-nom"
                      type="text"
                      placeholder="Tech Solutions RDC"
                      value={registerData.nom}
                      onChange={(e) => setRegisterData({ ...registerData, nom: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="company-register-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company-register-secteur">Secteur d'activité</Label>
                    <Input
                      id="company-register-secteur"
                      type="text"
                      placeholder="Technologie"
                      value={registerData.secteur}
                      onChange={(e) => setRegisterData({ ...registerData, secteur: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="company-register-sector-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="company-register-email">Email professionnel</Label>
                  <Input
                    id="company-register-email"
                    type="email"
                    placeholder="contact@entreprise.com"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="company-register-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="company-register-description">Description de l'entreprise</Label>
                  <Textarea
                    id="company-register-description"
                    placeholder="Décrivez votre entreprise..."
                    value={registerData.description}
                    onChange={(e) => setRegisterData({ ...registerData, description: e.target.value })}
                    required
                    className="mt-1"
                    rows={3}
                    data-testid="company-register-description-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company-register-password">Mot de passe</Label>
                    <Input
                      id="company-register-password"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="company-register-password-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company-register-confirm">Confirmer le mot de passe</Label>
                    <Input
                      id="company-register-confirm"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="company-register-confirm-password-input"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-6 font-semibold"
                  disabled={loading}
                  data-testid="company-register-submit-button"
                >
                  {loading ? 'Inscription...' : 'S\'inscrire'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </Card>

        <p className="text-center text-sm text-slate-600 mt-6">
          Publiez vos offres d'emploi et connectez-vous avec les meilleurs talents congolais.
        </p>
      </div>
    </div>
  );
};

export default CompanyAuth;
